// Firebase configuration for GEMS Live
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase (singleton pattern)
let app;
let auth;

export function getFirebaseApp() {
  if (!app) {
    app = getApps()[0] || initializeApp(firebaseConfig);
  }
  return app;
}

export function getFirebaseAuth() {
  if (!auth) {
    auth = getAuth(getFirebaseApp());
  }
  return auth;
}

// reCAPTCHA state
let recaptchaVerifier = null;
let recaptchaWidgetId = null;

export function clearRecaptcha() {
  if (recaptchaVerifier) {
    try {
      recaptchaVerifier.clear();
    } catch (e) {
      // ignore
    }
    recaptchaVerifier = null;
    recaptchaWidgetId = null;
  }
  // Also clean up any leftover reCAPTCHA DOM elements
  const container = document.getElementById('recaptcha-container');
  if (container) {
    container.innerHTML = '';
  }
}

export async function initRecaptcha() {
  const firebaseAuth = getFirebaseAuth();

  // Always start fresh to avoid stale state
  clearRecaptcha();

  // Ensure the container div exists
  let container = document.getElementById('recaptcha-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'recaptcha-container';
    document.body.appendChild(container);
  }

  recaptchaVerifier = new RecaptchaVerifier(firebaseAuth, 'recaptcha-container', {
    size: 'invisible',
    callback: () => {
      console.log('[Firebase] reCAPTCHA solved');
    },
    'expired-callback': () => {
      console.log('[Firebase] reCAPTCHA expired');
    }
  });

  try {
    recaptchaWidgetId = await recaptchaVerifier.render();
    console.log('[Firebase] reCAPTCHA rendered, widgetId:', recaptchaWidgetId);
  } catch (error) {
    console.error('[Firebase] reCAPTCHA render error:', error);
    clearRecaptcha();
    throw error;
  }

  return recaptchaVerifier;
}

export async function sendPhoneVerificationCode(phoneNumber) {
  const firebaseAuth = getFirebaseAuth();

  // Ensure reCAPTCHA is ready
  if (!recaptchaVerifier) {
    await initRecaptcha();
  }

  console.log('[Firebase] Sending verification to:', phoneNumber);
  const confirmationResult = await signInWithPhoneNumber(firebaseAuth, phoneNumber, recaptchaVerifier);

  // Store for later verification
  window.confirmationResult = confirmationResult;
  console.log('[Firebase] Code sent successfully');
  return { success: true };
}

export async function verifyPhoneCode(code) {
  if (!window.confirmationResult) {
    throw { code: 'auth/no-verification', message: 'No verification in progress. Please request a new code.' };
  }

  const result = await window.confirmationResult.confirm(code);
  const user = result.user;
  const idToken = await user.getIdToken();

  return {
    success: true,
    user: { uid: user.uid, phoneNumber: user.phoneNumber },
    idToken
  };
}

export async function getFirebaseIdToken() {
  const firebaseAuth = getFirebaseAuth();
  const user = firebaseAuth.currentUser;
  if (user) {
    return await user.getIdToken();
  }
  return null;
}

export { RecaptchaVerifier, signInWithPhoneNumber };
